import { Timer, Watch, Trash2 } from "lucide-react";

const fmt = (s) => {
  if (s == null || isNaN(s)) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const fmtLive = (s) => {
  if (s == null || isNaN(s)) return "0:00.0";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const tenth = Math.floor((s * 10) % 10);
  return `${m}:${String(sec).padStart(2, "0")}.${tenth}`;
};

// A render entry is {t: seconds, steps: N} (older entries were a bare number).
const secsOf = (e) => (typeof e === "number" ? e : e?.t);
const stepsOf = (e) => (typeof e === "number" ? null : e?.steps);

// Race-driver style "lap times" for renders. Newest first; green box = running average.
// Each lap shows how many steps that render was for, so users don't assume a 15-step video
// renders as fast as the average. When `live` is passed, a red running stopwatch shows on top.
export const RenderStopwatches = ({ times = [], live = null, onDelete }) => {
  const recent = times.slice(0, 5);
  const secs = times.map(secsOf).filter((s) => s != null && !isNaN(s));
  const avg = secs.length ? Math.round(secs.reduce((a, b) => a + b, 0) / secs.length) : null;
  const slots = Array.from({ length: 5 }, (_, i) => recent[i]);
  const running = live != null && live > 0;
  return (
    <div className="flex flex-col gap-1.5 w-[150px] shrink-0" data-testid="render-stopwatches">
      {running && (
        <div
          className="rounded-xl px-2 py-2 text-white text-center shadow-sm"
          style={{ background: "#1e3aaa", boxShadow: "0 0 12px rgba(30,58,170,0.55)" }}
          data-testid="live-render-time"
        >
          <div className="flex items-center justify-center gap-1 text-[9px] font-bold uppercase tracking-wide opacity-95 leading-tight">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Rendering
          </div>
          <div className="flex items-center justify-center gap-1 font-extrabold text-lg leading-none mt-1 tabular-nums">
            <Timer size={14} /> {fmtLive(live)}
          </div>
        </div>
      )}
      {slots.map((e, i) => {
        const t = secsOf(e);
        const st = stepsOf(e);
        return (
        <div
          key={i}
          data-testid={`render-lap-${i}`}
          className="rounded-lg pl-1 pr-2 py-1 flex items-center gap-1 text-white transition-colors"
          style={{ background: t != null ? "#4f7746" : "#cabff0" }}
        >
          {t != null && onDelete ? (
            <button
              type="button"
              onClick={() => onDelete(i)}
              data-testid={`render-lap-delete-${i}`}
              title="Remove this render time"
              aria-label={`Remove render ${i + 1}`}
              className="shrink-0 w-4 h-4 rounded flex items-center justify-center text-[#fecaca] hover:text-white hover:bg-[#dc2626] transition-colors"
            >
              <Trash2 size={11} />
            </button>
          ) : (
            <span className="shrink-0 w-4" />
          )}
          <span className="flex items-center gap-1 text-[9px] font-bold opacity-90 flex-1 min-w-0">
            <Watch size={9} className="shrink-0" /> {t != null && st ? `${st} steps` : `Render ${i + 1}`}
          </span>
          <span className="text-xs font-extrabold tabular-nums">{fmt(t)}</span>
        </div>
        );
      })}
      {/* Persistent average — same size as the red live stopwatch. Recomputed after every render. */}
      <div className="rounded-xl px-2 py-2 text-white text-center shadow-sm" style={{ background: "#16a34a" }} data-testid="avg-render-time">
        <div className="text-[9px] font-bold uppercase tracking-wide opacity-90 leading-tight">Average Time</div>
        <div className="flex items-center justify-center gap-1 font-extrabold text-lg leading-none mt-1">
          <Timer size={14} /> {fmt(avg)}
        </div>
      </div>
    </div>
  );
};
